import React, { useState, useEffect } from "react";
import "./MessageFeedback.css";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../contexts/AuthProvider";
import "bootstrap/dist/css/bootstrap.min.css";
import { Modal, Button } from "react-bootstrap";
import { registerFeedback } from "../services/FeedbackService";

function MessageFeedback({ messageId,
  messageFeedback,
  exportSummaryPdf,
  displayPatientSummaryPdfOption = false }) {
  const [copied, setCopied] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [customFeedback, setCustomFeedback] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [positiveFeedback, setPositiveFeedback] = useState(false);

  const { userToken } = useAuth();

  useEffect(() => {
    if (messageFeedback && Object.keys(messageFeedback).length > 0) {
      if (messageFeedback[0].feedbackType === "POSITIVE") {
        setPositiveFeedback(true);
      } else {
        setSelectedOption(messageFeedback[0].feedback);
        if (
          messageFeedback[0].feedback !== "Resposta incorreta" &&
          messageFeedback[0].feedback !== "Não seguiu as instruções por completo"
        ) {
          setCustomFeedback(messageFeedback[0].feedback);
          setSelectedOption("Outro");
        }
      }
    }
  }, [messageFeedback]);

  const handleCopy = () => {
    const messageElement = document.getElementById("message" + messageId);
    if (messageElement) {
      navigator.clipboard.writeText(messageElement.innerText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    }
  };

  const handleClick = async (feedback, type = "NEGATIVE") => {
    const isPositive = type === "POSITIVE";
    const isNegative = type === "NEGATIVE";

    const alreadyPositive = positiveFeedback;

    // Permitir sobrescrita apenas se for feedback oposto
    if ((isPositive && alreadyPositive) || (isNegative && selectedOption === feedback)) {
      return;
    }

    if (feedback === "Outro" && isNegative && !selectedOption) {
      setShowModal(true);
      return;
    }

    try {
      await registerFeedback(userToken, messageId, feedback, type);

      if (isPositive) {
        setPositiveFeedback(true);
        setSelectedOption(null);
        setCustomFeedback("");
        closeFeedbackOption();
      } else {
        setPositiveFeedback(false);
        setSelectedOption(feedback);
        if (feedback !== "Outro") setCustomFeedback("");
        closeFeedbackOption();
      }

      setErrorMessage("");
      setFeedbackSuccess(true);
      setTimeout(() => setFeedbackSuccess(false), 1000);
    } catch (error) {
      setErrorMessage("Erro ao salvar feedback. Tente novamente.");
      console.error("Erro ao registrar feedback:", error);
    }
  };

  const handleCustomFeedbackSubmit = async () => {
    if (!customFeedback.trim()) return;
    await handleClick(customFeedback, "NEGATIVE");
    setShowModal(false);
  };

  const openFeedbackOption = () => {
    const container = document.getElementById("feedback-options-container" + messageId);
    if (container) {
      container.classList.add("open");
      container.classList.remove("closed");
    }
  };

  const closeFeedbackOption = () => {
    const container = document.getElementById("feedback-options-container" + messageId);
    if (container) {
      container.classList.add("closed");
      container.classList.remove("open");
    }
  };

  const handleExportSummaryPdf = () => {
    if (exportSummaryPdf) {
      exportSummaryPdf();
    }
  };

  return (
    <div className="feedback-container chat-item">
      <div className="feedback-icons-container">
        <div className={`feedback-icon ${copied ? "copied" : ""}`} onClick={handleCopy}>
          {copied ? <CheckIcon /> : <ContentCopyIcon />}
        </div>

        {displayPatientSummaryPdfOption &&
          <div className="feedback-icon" onClick={handleExportSummaryPdf}>
            <PrintIcon />
          </div>
        }
        
        <div
          className={`feedback-icon ${positiveFeedback ? "selected" : ""}`}
          onClick={() => handleClick("Positive Feedback", "POSITIVE")}
          title="Gostei da resposta"
        >
          {positiveFeedback ? <ThumbUpAltIcon /> : <ThumbUpOffAltIcon />}
        </div>

        <div
          className={`feedback-icon ${feedbackSuccess ? "success-animation" : ""}`}
          onClick={openFeedbackOption}
          title={selectedOption ? selectedOption : "Clique para adicionar feedback"}
        >
          {!selectedOption ? <ThumbDownOffAltIcon /> : <ThumbDownAltIcon />}
        </div>
      </div>

      <div
        id={"feedback-options-container" + messageId}
        className="feedback-options-container closed card"
      >
        <div className="feedback-options-container-title">
          <div className="feedback-title">Explique melhor o motivo da insatisfação</div>
          <div onClick={closeFeedbackOption}>
            <CloseIcon />
          </div>
        </div>
        <div className="options">
          <div
            className={`option ${selectedOption === "Resposta incorreta" ? "selected" : ""}`}
            onClick={() => handleClick("Resposta incorreta", "NEGATIVE")}
          >
            Resposta incorreta
          </div>
          <div
            className={`option ${selectedOption === "Não seguiu as instruções por completo" ? "selected" : ""}`}
            onClick={() => handleClick("Não seguiu as instruções por completo", "NEGATIVE")}
          >
            Não seguiu as instruções por completo
          </div>
          <div
            className={`option ${selectedOption === "Outro" ? "selected" : ""}`}
            onClick={() => handleClick("Outro", "NEGATIVE")}
          >
            Outro
          </div>
        </div>
        {errorMessage && <div className="error-message">{errorMessage}</div>}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} className="custom-modal">
        <Modal.Header closeButton>
          <Modal.Title>Explique o motivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            type="text"
            className="form-control"
            placeholder="Detalhe sua insatisfação"
            value={customFeedback}
            id="feedback-input"
            onChange={(e) => setCustomFeedback(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <div className="modal-button-container">
            <Button className="modal-button" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button className="modal-button send-btn" onClick={handleCustomFeedbackSubmit}>
              Enviar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default MessageFeedback;
